// frontend/web-ui/src/components/lists/picker/PickerCard.tsx
import { Box, Flex, Image, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { CheckIcon } from "../../icons/CheckIcon";
import type { PickerItem } from "./types";

interface PickerCardProps {
  item: PickerItem;
  isAdded: boolean;
  onToggle: (item: PickerItem) => void;
  /** Disabled while a parent mutation is in flight. */
  isPending?: boolean;
}

export function PickerCard({
  item,
  isAdded,
  onToggle,
  isPending,
}: PickerCardProps) {
  const [t] = useTranslation();
  return (
    <Box
      as="button"
      type="button"
      onClick={() => !isPending && onToggle(item)}
      role="button"
      aria-pressed={isAdded}
      cursor={isPending ? "default" : "pointer"}
      opacity={isPending ? 0.6 : 1}
      textAlign="left"
      bg="transparent"
      border="none"
      p="0"
      _focusVisible={{
        outline: "2px solid",
        outlineColor: "blaze.blaze",
        outlineOffset: "2px",
      }}
    >
      <Box
        position="relative"
        w="100%"
        pb="150%" /* 2:3 aspect */
        bg="transparent.white.05"
        borderRadius="6px"
        overflow="hidden"
      >
        {item.coverUrl && (
          <Image
            src={item.coverUrl}
            alt=""
            position="absolute"
            inset="0"
            w="100%"
            h="100%"
            objectFit="cover"
            transition="opacity 150ms ease-out"
            opacity={isAdded ? 0.55 : 1}
          />
        )}
        {isAdded && (
          <Flex
            data-testid="picker-card-check"
            position="absolute"
            top="6px"
            right="6px"
            w="22px"
            h="22px"
            align="center"
            justify="center"
            bg="rgba(0,171,101,0.95)"
            borderRadius="999px"
            color="white"
            aria-label={t("screens.shelf.readingLists.picker.addedCheckAria")}
          >
            <CheckIcon w="12px" h="12px" />
          </Flex>
        )}
        {isAdded && (
          <Box
            position="absolute"
            inset="0"
            border="2px solid"
            borderColor="rgba(0,171,101,0.9)"
            borderRadius="6px"
            pointerEvents="none"
          />
        )}
      </Box>
      {/*
        Both lines cap at 2 with ellipsis. We also set the line-clamp
        explicitly via `sx` because Chakra's `noOfLines` shorthand can be
        suppressed by the `as="button"` ancestor's user-agent display
        styles (some browsers reset `display` on button content), which
        was leaving long titles + verbose subtitles wrapping to 3+ lines
        in the wild.
      */}
      <Text
        mt="6px"
        fontSize="13px"
        fontWeight={600}
        color="dune.dune"
        wordBreak="break-word"
        sx={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
        }}
      >
        {item.title}
      </Text>
      <Text
        fontSize="11px"
        color="dune.700"
        wordBreak="break-word"
        sx={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
        }}
      >
        {item.subtitle}
      </Text>
    </Box>
  );
}
